<template>
  <div>
    <div
      ref="map"
      class="map"
    >
      <slot />
    </div>
  </div>
</template>

<script>
import { loadCss, loadModules } from 'esri-loader'

import { CENTER, ZOOM, ZOOM_MIN } from '@/fixtures/Constants'
import EventBus from '@/plugins/EsriEventBus'

export default {
  props: {
    layers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      map: null,
      view: null,
      classes: {
        Map: null,
        MapView: null,
        GraphicsLayer: null,
        Graphic: null,
        Circle: null,
        Polyline: null
      },
      pointerCoordinates: {
        longitude: 0,
        latitude: 0
      }
    }
  },
  mounted() {
    EventBus.$on('map-add-graphics', this.addGraphics);
    EventBus.$on('map-add-lines', this.addLines);
    EventBus.$on('map-remove-graphics', this.removeGraphics);
    EventBus.$on('map-add-areas', this.addAreas);
    EventBus.$on('map-set-view', this.setView);
    this.loadMap();
  },
  beforeDestroy() {
    EventBus.$off('map-set-view', this.setView);
  },
  methods: {
    async loadMapAssets() {
      await loadCss('https://js.arcgis.com/4.9/esri/css/main.css');
      const [
        Map,
        MapView,
        GraphicsLayer,
        Graphic,
        Circle,
        Polyline
      ] = await loadModules([
        'esri/Map',
        'esri/views/MapView',
        'esri/layers/GraphicsLayer',
        'esri/Graphic',
        'esri/geometry/Circle',
        'esri/geometry/Polyline'
      ], {
        url: 'https://js.arcgis.com/4.9/'
      });
      this.classes.Map = Map;
      this.classes.MapView = MapView;
      this.classes.GraphicsLayer = GraphicsLayer;
      this.classes.Graphic = Graphic;
      this.classes.Circle = Circle;
      this.classes.Polyline = Polyline;
    },
    async loadMap() {
      await this.loadMapAssets()
      this.map = new this.classes.Map({
        basemap: 'streets'
      });
      this.view = new this.classes.MapView({
        map: this.map,
        center: CENTER,
        zoom: ZOOM,
        container: this.$refs.map,
        constraints: {
          rotationEnabled: false,
          minZoom: ZOOM_MIN
        }
      });
      this.view.ui.move(['zoom'], 'bottom-right');
      this.view.on('click', this.onMapClick);
      this.map.addMany(this.layers.map(this.createGraphicsLayer));
      this.view.when(() => {
        EventBus.$emit('map-ready');
      });
      this.view.on('pointer-move', this.onPointerMove);
    },
    async onMapClick({ x, y }) {
      const { results } = await this.view.hitTest({ x, y })
      if (results.length) {
        await this.showDetail(results[0])
        EventBus.$emit('map-on-click', {
          map: this.map,
          view: this.view,
          results
        })
        return
      }
      this.$emit('close-detail')
    },
    async showDetail({ graphic }) {
      if (!graphic) {
        return
      }

      const { longitude, latitude } = graphic.geometry.center || graphic.geometry
      await this.view.goTo({ center: [longitude, latitude] })
      this.$emit('show-detail', graphic.attr)
    },
    onPointerMove(ev) {
      const { longitude, latitude } = this.view.toMap(ev)
      this.pointerCoordinates = {
        longitude: longitude.toFixed(7),
        latitude: latitude.toFixed(7)
      }
    },
    addGraphics({ layerId, graphics }) {
      this.map
        .findLayerById(layerId)
        .graphics
        .addMany(graphics.map(this.createGraphic));
    },
    addLines({ layerId, lines }) {
      this.map
        .findLayerById(layerId)
        .graphics
        .addMany(lines.map(this.createPolyline));
    },
    removeGraphics({ layerIds }) {
      for (let i = 0; i < layerIds.length; i++) {
        this.map
          .findLayerById(layerIds[i])
          .graphics
          .removeAll();
      }
    },
    addAreas({ layerId, graphics }) {
      this.map
        .findLayerById(layerId)
        .graphics
        .addMany(graphics.map(this.createAreaGraphic));
    },
    createGraphicsLayer(id) {
      return new this.classes.GraphicsLayer({ id });
    },
    createGraphic(config) {
      return new this.classes.Graphic(config);
    },
    createPolyline(config) {
      return new this.classes.Graphic({
        ...config,
        geometry: new this.classes.Polyline(config.geometry)
      });
    },
    createAreaGraphic(config) {
      return new this.classes.Graphic({
        ...config,
        geometry: new this.classes.Circle(config.geometry)
      });
    },
    setView({ center, zoom }) {
      if (this.view && center) {
        this.view.when(() => {
          this.view.goTo({ center, zoom: zoom || 14 });
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .esri-attribution {
  display: none;
}

.map {
  width: 100%;
  height: 100%;
  position: relative;
}
</style>
